import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { CompanyDetailedDtoQueryResultDto } from 'models/dtoTypes';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { CompanySearch, CompanySearchByAddress, GetOptionSet } from 'services/openapiClient';
import Pagination from '@mui/material/Pagination';
import AdvancedCompanySearchWidget from 'components/AdvancedCompanySearchWidget';
import { CurrentUser } from 'auth';
import CloseIcon from '@mui/icons-material/Close';
import KosmosCompanyResultWithSmartGovernance from 'kosmos/components/KosmosCompanyResultWithSmartGovernance';


const PREFIX = 'CompanySearchWidget';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  red: `${PREFIX}-red`,
  green: `${PREFIX}-green`,
  appBar: `${PREFIX}-appBar`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  box: `${PREFIX}-box`,
  iconButton: `${PREFIX}-iconButton`,
  divider: `${PREFIX}-divider`,
  loadingBox: `${PREFIX}-loadingBox`,
  resultContainer: `${PREFIX}-resultContainer`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    width: '100%',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    '& > *': {
      flex: 1
    },
  },

  [`& .${classes.red}`]: {
    backgroundColor: red,
  },

  [`& .${classes.green}`]: {
    backgroundColor: green,
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
  },

  [`& .${classes.contentContainer}`]: {
    width: '100%',
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    textAlign: 'left',
    padding: theme.spacing(1),
    paddingLeft: 0,
    overflowX: 'hidden',
  },

  [`& .${classes.title}`]: {
    lineHeight: 1
  },

  [`& .${classes.box}`]: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    minWidth: 800,
    '& > *': {
      marginRight: 15,
    }
  },

  [`& .${classes.iconButton}`]: {
    padding: 0,
    height: 40,
    width: 120,
    fontSize: 16
  },

  [`& .${classes.divider}`]: {
    height: 28,
    margin: 4,
  },
  [`& .${classes.loadingBox}`]: {
    width: '100%',
  },

  [`& .${classes.resultContainer}`]: {
    marginTop: 20,
    marginBottom: 20,
    '& .pagination': {
      marginBottom:  110,
    }
  },
}));

const red = '#FF75A8';
const green = '#26C9A8';

export interface IMessage {
  message?: string;
  severity?: 'error' | 'info';
}

export interface ICompanySearchResult {
  loading: boolean;
  CompaniesWithPageMeta?: CompanyDetailedDtoQueryResultDto;
  messages: IMessage[];
}

interface Props {
  placeholder?: string;
  title?: string;
  onShowOutOfCreditsNotice: () => void;
  onSelectCompany?: (id: string) => void;
  onSelectSgCompany?: (id: string) => void;
}

interface ICountryOption {
  label: string;
  alpha3: string;
}

const PAGE_SIZE = 25;
const ALL_COUNTRIES = {
  label: 'All Countries',
  alpha3: ''
};

interface IFilters {
  addressRegion?: string;
  streetAddressLine1?: string;
  addressLocality?: string;
  postalCode?: string;
  radiusLat?: number;
  radiusLon?: number;
  radiusPostalCode?: string;
  radiusDistance?: number;
  radiusUnit?: string;
}

export default function CompanySearchWidget(props: Props): JSX.Element {
  const currentUser = CurrentUser.getInstance();
  const {
    placeholder = 'Search by company name',
    title = 'Company Search',
    onSelectCompany,
    onSelectSgCompany,
    onShowOutOfCreditsNotice,
  } = props;

  const [tabName, setTabName] = useState<'address' | 'geo'>('address');
  const [result, setResult] = React.useState<ICompanySearchResult>({
    loading: false,
    CompaniesWithPageMeta: undefined,
    messages: [],
  });
  const [orderBy, setOrderBy] = useState('+name');
  console.log(setOrderBy);
  const [currentPage, setCurrentPage] = useState(1);
  const [countries, setCountries] = useState<ICountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<ICountryOption>(ALL_COUNTRIES);
  const [query, setQuery] = useState<string>('');
  const [externalIdentifier, setExternalIdentifier] = useState<string>('');

  const [openAdvancedSearch, setOpenAdvancedSearch] = useState(false);
  const [filters, setFilters] = useState<IFilters>({});

  const handleChangeFilter = (parts: Partial<IFilters>) => {
    const newFilters = {
      ...filters,
      ...parts,
    };
    setFilters(newFilters);
  };

  useEffect(() => {
    const fetchCountries = async () => {
      const options = await GetOptionSet('countries');
      const formattedOptions = options.map(item => ({
        label: item.label || '',
        alpha3: item.alias || ''
      }));
      formattedOptions.unshift(ALL_COUNTRIES);
      const selected = formattedOptions.find(o => o.alpha3 === currentUser.getScope('country'));
      if (selected) {
        setSelectedCountry(selected);
      }
      setCountries(formattedOptions);
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (query) {
      fetchCompanies(currentPage - 1);
    } else {
      setResult({
        loading: false,
        messages: [{
          message: 'Enter a search term to find companies',
          severity: 'info'
        }],
      });
    }
  }, [currentPage, orderBy]);

  const handleChangeQuery = (e: { target: { name: string; value: string } }) => {
    setQuery(e.target.value);
  };

  const handleChangeExternalIdentifier = (e: { target: { name: string; value: string } }) => {
    setExternalIdentifier(e.target.value);
  };

  const fetchCompaniesByAddress = async (pageIndex: number) => {
    if (query.length < 3 && !externalIdentifier) {
      setResult({
        loading: false,
        messages: [{
          message: 'Type at least 3 characters',
          severity: 'error'
        }],
      });
      return;
    }
    setResult({
      loading: true,
      messages: []
    });
    if (!filters?.streetAddressLine1) {
      setResult({
        loading: false,
        messages: [{
          message: 'street line is required',
          severity: 'error'
        }],
      });
      return;
    }
    try {
      const countryCode = selectedCountry.alpha3;
      const {
        addressRegion,
        addressLocality,
        postalCode,
        streetAddressLine1,
      } = filters;
      const companiesWithPageMeta = await CompanySearchByAddress({
        query,
        countryCode3: countryCode,
        addressRegion,
        addressLocality,
        postalCode,
        streetAddressLine1,
        pageIndex,
        pageSize: PAGE_SIZE,
        order: orderBy
      });
      const hasCompany = companiesWithPageMeta?.items && companiesWithPageMeta.items.length > 0;
      const messages = [];
      if (!hasCompany) {
        messages.push({
          message: 'No record found',
          severity: 'info' as 'info' | 'error'
        });
      }
      setResult({
        loading: false,
        CompaniesWithPageMeta: companiesWithPageMeta,
        messages,
      });
    } catch(e) {
      setResult({
        loading: false,
        CompaniesWithPageMeta: undefined,
        messages: [{
          message: e instanceof Error ? e.message : String(e),
          severity: 'error'
        }]
      });
    }
  };
  const fetchCompanies = async (pageIndex: number) => {
    if (query.length < 3 && !externalIdentifier) {
      setResult({
        loading: false,
        messages: [{
          message: 'Type at least 3 characters',
          severity: 'error'
        }],
      });
      return;
    }
    setResult({
      loading: true,
      messages: []
    });
    try {
      const countryCode = selectedCountry.alpha3;
      const companiesWithPageMeta = await CompanySearch({
        externalIdentifier,
        query,
        countryCode3: countryCode,
        pageIndex,
        pageSize: PAGE_SIZE,
        order: orderBy
      });
      const hasCompany = companiesWithPageMeta?.items && companiesWithPageMeta.items.length > 0;
      const messages = [];
      if (!hasCompany) {
        messages.push({
          message: 'No record found',
          severity: 'info' as 'info' | 'error'
        });
      }
      setResult({
        loading: false,
        CompaniesWithPageMeta: companiesWithPageMeta,
        messages,
      });
    } catch(e) {
      setResult({
        loading: false,
        CompaniesWithPageMeta: undefined,
        messages: [{
          message: e instanceof Error ? e.message : String(e),
          severity: 'error'
        }]
      });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentPage === 1) {
      // always fetch the first page
      openAdvancedSearch ? fetchCompaniesByAddress(0) : fetchCompanies(0);
    } else {
      setCurrentPage(1);
    }
  };

  const handleReset = () => {
    setQuery('');
    setExternalIdentifier('');
    setSelectedCountry(ALL_COUNTRIES);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleChangeCountry = (event: unknown, newValue: ICountryOption | null) => {
    const selected = newValue || ALL_COUNTRIES;
    CurrentUser.getInstance().setScope('country', selected.alpha3);
    setSelectedCountry(selected);
  };

  const renderLoading = () => {
    return (
      <Box className={classes.loadingBox}>
        <Skeleton height={100}/>
        <Skeleton height={100}/>
        <Skeleton height={100}/>
      </Box>
    );
  };

  const renderResultMessage = () => {
    const { messages } = result;
    const warnMsg = 'Worldwide searches may take several minutes';
    const formattedMessages = [...messages];
    if (!selectedCountry?.alpha3) {
      formattedMessages.push({
        message: warnMsg,
        severity: 'info'
      });
    }
    if (formattedMessages?.length) {
      return (
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          {formattedMessages.map((m, i) => <Alert key={i} severity={m.severity || 'error'}>{m.message}</Alert>)}
        </Stack>
      );
    } else {
      return <></>;
    }
  };

  const renderResult = () => {
    const { CompaniesWithPageMeta = {}, loading } = result;
    const { items, pageCount, count } = CompaniesWithPageMeta;
    if (loading)
      return renderLoading();
    else if (items && items.length > 0) {
      return (
        <div>
          <KosmosCompanyResultWithSmartGovernance
            count={count}
            companies={items}
            onOutOfCredits={onShowOutOfCreditsNotice}
            onSelectCompany={onSelectCompany}
            onSelectSgCompany={onSelectSgCompany}
          />
          <div className="pagination">
            <Pagination
              count={pageCount}
              defaultPage={currentPage}
              onChange={handleChangePage}
            />
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const handleTabChange = (name: 'address' | 'geo') => {
    setTabName(name);
  };

  return (
    <Root className={classes.root}>
      <main className={classes.contentContainer}>
        <div className={classes.content}>
          {title && (
            <h2 className={classes.title}>{title}</h2>
          )}
          <Box
            component="form"
            onSubmit={handleSearch}
          >
            <Box className={classes.box}>
              <TextField
                value={query}
                disabled={result.loading || !!externalIdentifier.length}
                onChange={handleChangeQuery}
                placeholder={placeholder}
                inputProps={{ 'aria-label': 'Search by company name' }}
                sx={{ width: 300 }}
                size="small"
              />
              <Autocomplete
                disabled={countries.length < 1 || result.loading || !!externalIdentifier.length}
                id=""
                value={selectedCountry}
                onChange={handleChangeCountry}
                options={countries}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a country"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <TextField
                value={externalIdentifier}
                disabled={result.loading || !!query.length || !!selectedCountry.alpha3.length}
                onChange={handleChangeExternalIdentifier}
                placeholder={'Search by External Identifier'}
                inputProps={{ 'aria-label': 'Search by external identifier' }}
                sx={{ width: 300 }}
                size="small"
              />
              <Button
                type="submit" // bind to Enter key
                disabled={result.loading}
                onClick={handleSearch}
                className={classes.iconButton}
                aria-label="search"
                variant="contained"
                color="primary"
                endIcon={<SearchIcon />}
              >
                Search
              </Button>
              <Button
                disabled={result.loading}
                onClick={handleReset}
                className={classes.iconButton}
                aria-label="reset"
                variant="contained"
                color="primary"
                endIcon={<CloseIcon />}
              >
                Reset
              </Button>
              <Link
                variant="body2"
                onClick={() => setOpenAdvancedSearch(!openAdvancedSearch)}
              >
                {openAdvancedSearch ? 'Reset filters' : 'Advanced'}
              </Link>
            </Box>
            <Box>
              <Collapse in={openAdvancedSearch}>
                <AdvancedCompanySearchWidget
                  countryCodeAlpha3={selectedCountry.alpha3}
                  onChangeFilter={handleChangeFilter}
                  tabName={tabName}
                  onTabChange={handleTabChange}
                  filters={filters}
                />
              </Collapse>
            </Box>
          </Box>
        </div>
        <div className={classes.resultContainer}>
          {renderResultMessage()}
          {renderResult()}
        </div>
      </main>
    </Root>
  );
}
